/** @jsxImportSource @emotion/react */

import { FC, useMemo } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { useQuery } from '@apollo/client'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { FIND_ALL_SUCCESS_STORIES } from 'queries/successStory'
import { findAllSuccessStory, findAllSuccessStoryVariables } from '__generatedTypes__/findAllSuccessStory'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { SelectField } from 'components/Select'
import { validateRequired } from 'utils/validation'
import { FIND_ALL_STUDY_GUIDE } from 'queries/studyGuide'
import { findAllStudyGuide, findAllStudyGuideVariables } from '__generatedTypes__/findAllStudyGuide'
import * as classes from './styles'

export type SelectItem = {
  id: string
  title: string
}

type Props = {
  platform: Platform
  fields: FieldArrayRenderProps<SelectItem | undefined, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<SelectItem | undefined, HTMLElement>['meta']
  entityId?: string
  isSuccessStory?: boolean
}

export const SimilarPosts: FC<Props> = ({ fields, platform, entityId, isSuccessStory = false }) => {
  const { data: successStories, loading: successStoriesLoading } = useQuery<
    findAllSuccessStory,
    findAllSuccessStoryVariables
  >(FIND_ALL_SUCCESS_STORIES, {
    fetchPolicy: 'network-only',
    variables: {
      query: {
        take: TAKE_ALL_ITEMS_COUNT,
        where: {
          platform: {
            id: platform.id,
          },
        },
      },
    },
    skip: !isSuccessStory,
  })

  const { data: studyGuides, loading: studyGuidesLoading } = useQuery<findAllStudyGuide, findAllStudyGuideVariables>(
    FIND_ALL_STUDY_GUIDE,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
      skip: isSuccessStory,
    }
  )

  const similarPosts = useMemo(() => {
    const posts = isSuccessStory ? successStories?.findAllSuccessStory?.items : studyGuides?.findAllStudyGuide?.items

    const existingIds = (fields.value ?? []).map((field) => field?.id).filter((id) => !!id)
    const postItems = (posts || []).map(({ id, title }) => ({
      id,
      title,
    }))

    return postItems.filter(({ id }) => ![...existingIds, entityId].includes(id))
  }, [successStories, studyGuides, fields])

  return (
    <div css={classes.similarPostsWrapper}>
      {fields.map((field, i) => (
        <SelectField<SelectItem>
          key={field}
          name={field}
          validate={validateRequired}
          items={similarPosts}
          loading={successStoriesLoading || studyGuidesLoading}
          label={`Similar Post ${i + 1}`}
          placeholder="Choose similar post"
          displaySelectedItemsFirst
          fullWidth
        />
      ))}
    </div>
  )
}
